import { Component } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { BaseUpsertComponent } from '@cl/core/components';
import { AuthService } from '@cl/core/services';
import { Absence } from '@cl/shared/models/absences/absence.model';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { DATE_FORMAT, MOMENT_DATE_FORMAT } from '@cl/shared/constants';
import { GroupPermissionsType } from '@cl/shared/group-permissions-type.enum';
import moment, { Moment } from 'moment';
import { AbsencesService } from '@cl/core/services/absences.service';
import { differenceInCalendarDays } from 'date-fns';
import { AsyncValidatorService } from '@cl/core/validators/async-validator.service';
import { EntityType } from '@cl/shared/models';

@Component({
    selector: 'cl-upsert-absence',
    standalone: false,
    templateUrl: './upsert-absence.component.html',
    styleUrl: './upsert-absence.component.less',
})
export class UpsertAbsenceComponent extends BaseUpsertComponent<Absence, AbsencesService> {
    DATE_FORMAT: string = DATE_FORMAT;
    MOMENT_DATE_FORMAT: string = MOMENT_DATE_FORMAT;
    EntityType = EntityType;

    private dateSelectAllowedFrom: Moment = moment().add(1, 'days').startOf('day');

    private initialRangeDays: number = 0;
    private previousEndDate: Date;

    get hasMenuBookkeeperPermission(): boolean {
        return this.authService.userHasPermission([GroupPermissionsType.MENU_BOOKKEEPER]);
    }

    constructor(
        protected absencesService: AbsencesService,
        protected messageService: NzMessageService,
        private modal: NzModalRef,
        private fb: UntypedFormBuilder,
        private authService: AuthService,
        private modalService: NzModalService,
        private asyncValidatorService: AsyncValidatorService,
    ) {
        super(absencesService, messageService);

        // set modal texts
        this.saveSuccessText = 'Prombūtne saglabāta!';
        this.createObjectSaveErrorText = 'Kļūda pievienojot prombūtni';
        this.updateObjectSaveErrorText = 'Kļūda labojot prombūtni';
    }

    createForm(): UntypedFormGroup {
        return this.fb.group({});
    }

    createAndFillForm(obj: Absence): UntypedFormGroup {
        // Calculate the initial range in days
        const startDate: Moment = moment(this.originalObject.startDate);
        const endDate: Moment = moment(this.originalObject.endDate);
        this.initialRangeDays = endDate.diff(startDate, 'days'); // +1 to include the start date in the range

        return this.fb.group({
            startDate: [this.originalObject.startDate, [Validators.required]],
            endDate: [
                this.originalObject.endDate,
                [Validators.required],
                [
                    this.asyncValidatorService.absenceDateRangeOverlapValidator(
                        this.originalObject.performerId,
                        this.originalObject.id,
                        this.originalObject.startDate,
                        this.originalObject.endDate,
                    ),
                ],
            ],
        });
    }

    afterInit(): void {
        if (!this.objectForm || !this.isEditMode) {
            return;
        }

        const startDateControl: AbstractControl = this.objectForm.get('startDate');
        const endDateControl: AbstractControl = this.objectForm.get('endDate');

        if (!startDateControl || !endDateControl) {
            return;
        }

        // Store initial values
        this.previousEndDate = endDateControl.value;

        startDateControl.valueChanges.subscribe((startDate: Date): void => {
            if (!startDate) {
                return;
            }

            if (!this.hasMenuBookkeeperPermission) {
                this.previousEndDate = endDateControl.value;
                const newEndDate = moment(startDate).add(this.initialRangeDays, 'days');
                endDateControl.setValue(newEndDate.toDate(), { emitEvent: false });
            }

            // Re-check the overlap validator on endDate since its sibling (startDate) changed
            endDateControl.updateValueAndValidity();
        });

        endDateControl.valueChanges.subscribe((endDate: Date): void => {
            if (!endDate) {
                return;
            }

            if (!this.hasMenuBookkeeperPermission) {
                const newStartDate: Moment = moment(endDate).subtract(this.initialRangeDays, 'days');

                if (newStartDate.isBefore(this.dateSelectAllowedFrom)) {
                    this.modalService.warning({
                        nzContent: `Lai saglabātu prombūtnes ilgumu, beigu datums nedrīkst būt agrāks par ${this.dateSelectAllowedFrom.clone().add(this.initialRangeDays, 'days').format(MOMENT_DATE_FORMAT)}. Lūdzu, izvēlieties citu beigu datumu.`,
                        nzOkText: 'Aizvērt',
                    });

                    // Revert to previous value
                    endDateControl.setValue(this.previousEndDate, { emitEvent: false });
                    return;
                }

                startDateControl.setValue(newStartDate.toDate(), { emitEvent: false });
                this.previousEndDate = endDate;
            }
        });
    }

    disabledDate = (current: Date): boolean => {
        if (this.hasMenuBookkeeperPermission) {
            return false;
        }

        // Can not select days before today and today
        return (
            differenceInCalendarDays(current, this.dateSelectAllowedFrom.toDate()) < 0 ||
            differenceInCalendarDays(current, moment().endOf('year').toDate()) > 0
        );
    };

    submitAndReturn(val: boolean | Absence): void {
        this.modal.close(val);
    }

    cancel(): void {
        this.modal.destroy();
    }
}
